import { CSSProperties, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { colors } from '@lmc/common/constants'
import { IconType } from '../../general/Icon/iconTypes'
import Icon from '../../general/Icon'
import { Button } from '../../general/Button'
import InputText from '../InputText'
import { ConsentFormTemplate } from '@lmc/common/types'
import { isMobile as isMobileDevice } from 'react-device-detect'
import {
  RichEditor,
  normalizeRichEditorHtmlForStorage,
} from '../../dataEntry/RichEditor'

export type LabelTextBoxProps = {
  content: any
  copyOfTemplates?: ConsentFormTemplate[]
  customEntry?: boolean
  id?: string
  isMobile?: boolean
  localisation: {
    title: string
    subTitle?: string
    readOnly: string
    cancel?: string
    save?: string
    clickToAddTitle?: string
    loseText?: string
  }
  state: 'display' | 'read-only'
  style?: CSSProperties
  onCancel?: () => void
  onEdit?: () => void
  onTitleChange?: (e: any) => void
  setCopyOfTemplates?: (e: any) => void
}

const getTitle = (title: string, customTitle: string, customEntry: boolean) => {
  if (customEntry) {
    return customTitle === '' ? 'Custom Title' : customTitle
  }
  return title
}

const toHtmlString = (value: unknown): string =>
  typeof value === 'string' ? normalizeRichEditorHtmlForStorage(value) : ''

const LabelTextBox = ({
  content,
  localisation,
  state,
  style,
  onEdit,
  onCancel,
  id,
  setCopyOfTemplates,
  copyOfTemplates,
  customEntry,
  isMobile: isMobileProp,
}: LabelTextBoxProps) => {
  const isReadOnly = state === 'read-only'
  const isLayoutMobile = isMobileProp ?? isMobileDevice

  const [editableMode, setEditableMode] = useState(false)

  const [warning, setWarning] = useState(false)

  const [customTitle, setCustomTitle] = useState(
    copyOfTemplates?.find((item) => item.id === id)?.title
  )

  const [contentCopy, setContentCopy] = useState(toHtmlString(content))

  /** HTML while the TipTap editor is open (draft). */
  const [draftBody, setDraftBody] = useState(toHtmlString(content))

  // When editing consent templates (`setCopyOfTemplates`), only sync from `content`
  // when `id` changes — same as the legacy Draft.js `LabelTextBox`. Parent `content`
  // can lag `copyOfTemplates` after save if callers pass a stale template list.
  useEffect(() => {
    if (!setCopyOfTemplates) return
    const html = toHtmlString(content)
    setContentCopy(html)
    setDraftBody(html)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: do not depend on `content` (stale after save when id unchanged)
  }, [id, setCopyOfTemplates])

  useEffect(() => {
    if (setCopyOfTemplates) return
    const html = toHtmlString(content)
    if (!editableMode) {
      setContentCopy(html)
      setDraftBody(html)
    }
  }, [content, editableMode, setCopyOfTemplates])

  const savedTitle = copyOfTemplates?.find((item) => item.id === id)?.title
  const titleDirty =
    Boolean(customEntry) &&
    editableMode &&
    String(customTitle ?? '') !== String(savedTitle ?? '')
  const bodyDirty = draftBody !== contentCopy
  const hasChangedContent = bodyDirty || titleDirty

  const handleEdit = () => {
    onEdit?.()
    setDraftBody(contentCopy)
    setEditableMode(!editableMode)
  }

  const handleCancel = () => {
    if (hasChangedContent) {
      setWarning(true)
    }
    if (!hasChangedContent || warning) {
      onCancel?.()
      setEditableMode(!editableMode)
      const reset = toHtmlString(contentCopy)
      setDraftBody(reset)
      setCustomTitle(copyOfTemplates?.find((item) => item.id === id)?.title)
      setWarning(false)
    }
  }

  const handleSave = () => {
    setEditableMode(!editableMode)
    setWarning(false)
    setContentCopy(draftBody)
    const newTemplates = copyOfTemplates?.map((item) => {
      if (item.id === id) {
        return {
          ...item,
          body: draftBody,
          // @ts-ignore
          title: getTitle(item.title, customTitle, customEntry),
        }
      }
      return item
    })
    setCopyOfTemplates?.(newTemplates)
  }

  const handleUpdateTitle = (e: any) => {
    setCustomTitle?.(e.target.value)
  }

  const handleRemove = () => {
    setWarning(true)
    if (warning) {
      const filteredCustomForms = copyOfTemplates?.filter(
        (item) => item.id !== id
      )
      setCopyOfTemplates?.(filteredCustomForms)
      setWarning(false)
    }
  }

  const onRichEditorChange = (html?: string) => {
    setDraftBody(html ?? '')
  }

  return (
    <Container
      id="label-text-box"
      style={style}
      $isReadOnly={isReadOnly}
    >
      <TitleRow>
        <TitleContent $titleInput={Boolean(customEntry && editableMode)}>
          {!customEntry || !editableMode ? (
            localisation.title
          ) : (
            <InputText
              id="title-input-editor"
              // eslint-disable-next-line no-return-assign
              value={customTitle}
              onChange={handleUpdateTitle}
              placeholder={
                localisation?.clickToAddTitle || 'Click to add a Title'
              }
            />
          )}
        </TitleContent>
        {!isReadOnly && (
          <>
            {!customEntry && warning && (
              <LoseText>{localisation.loseText}</LoseText>
            )}
            {customEntry && (
              <DeleteTrigger
                $editableMode={editableMode}
                onClick={() => handleRemove()}
              >
                {warning && <LoseText>{localisation.loseText}</LoseText>}
                <Icon name="DeleteFilled" />
              </DeleteTrigger>
            )}
            {!editableMode && (
              <EditTrigger
                id="edit-button"
                $withPencil={Boolean(customEntry)}
                onClick={() => handleEdit()}
              >
                <Icon customIcon={IconType.Pencil} />
              </EditTrigger>
            )}
          </>
        )}
        {isReadOnly && (
          <ReadOnlyBadge>{localisation.readOnly}</ReadOnlyBadge>
        )}
      </TitleRow>
      {localisation.subTitle && (
        <SubTitle>{localisation.subTitle}</SubTitle>
      )}
      {!isReadOnly && editableMode && (
        <EditActionsRow $isMobile={isLayoutMobile}>
          <Button
            id="cancel-button"
            type="button"
            variant="default"
            onClick={() => handleCancel()}
          >
            {localisation.cancel}
          </Button>
          {hasChangedContent && (
            <Button
              id="save-button"
              type="button"
              variant="primary"
              icon={<Icon name="CheckOutlined" />}
              onClick={() => handleSave()}
            >
              {localisation.save}
            </Button>
          )}
        </EditActionsRow>
      )}
      {!isReadOnly &&
        (editableMode ? (
          <EditorWrap>
            <RichEditor
              html={draftBody}
              onChangeHtml={onRichEditorChange}
              readOnly={false}
            />
          </EditorWrap>
        ) : (
          <HtmlContent
            dangerouslySetInnerHTML={{ __html: contentCopy }}
          />
        ))}
      {isReadOnly && (
        <HtmlContent
          dangerouslySetInnerHTML={{ __html: content }}
        />
      )}
    </Container>
  )
}

const Container = styled.div<{ $isReadOnly: boolean }>`
  background-color: ${({ $isReadOnly }) => $isReadOnly ? colors.lightgrey : colors.white};
  border-radius: 15px;
  padding: 16px;
  width: 100%;
`

const TitleRow = styled.div`
  display: flex;
  position: relative;

  font-size: 15px;
  font-weight: 400;
`

const TitleContent = styled.div<{ $titleInput: boolean }>`
  opacity: 0.6;
  ${({ $titleInput }) =>
    $titleInput &&
    css`
      height: 32px;
    `}
`

const SubTitle = styled.div`
  font-size: 14px;
  font-weight: 300;
  opacity: 0.6;
`

const headerActionBase = css`
  display: flex;
  font-weight: 700;
  margin-left: auto;
  margin-right: 0;
`

const ReadOnlyBadge = styled.div`
  ${headerActionBase};
  opacity: 0.6;
`

const EditTrigger = styled.div<{ $withPencil: boolean }>`
  ${headerActionBase};
  cursor: pointer;
  ${({ $withPencil }) =>
    $withPencil &&
    css`
      margin-left: 10px;
    `}
`

const DeleteTrigger = styled.div<{ $editableMode: boolean }>`
  ${headerActionBase};
  cursor: pointer;
  ${({ $editableMode }) =>
    $editableMode &&
    css`
      position: absolute;
      right: 20px;
    `}
`

const LoseText = styled.div`
  color: ${colors.red};
  font-weight: 700;
  margin: -3px 4px 0 auto;
`

const EditActionsRow = styled.div<{ $isMobile: boolean }>`
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  gap: 8px;
  justify-content: ${({ $isMobile }) => $isMobile ? 'flex-start' : 'flex-end'};
  margin-top: 10px;
  width: 100%;
  z-index: 10;
`

const EditorWrap = styled.div`
  background-color: ${colors.white};
  border-radius: 4px;
  border: 1px solid ${colors.informationblue};
  margin-top: 20px;
  opacity: 0.7;
  padding: 18px;
`

const HtmlContent = styled.div`
  background: ${colors.white};
  border-radius: 4px;
  border: 1px solid ${colors.grey};
  margin-top: 20px;
  opacity: 0.7;
  padding: 18px;

  /* Raw HTML: empty <p></p> has no height in browsers; keep blank lines visible */
  p:empty {
    min-height: 1.5em;
  }
`

export default LabelTextBox
